import { publicApi } from ".";

interface IPaymentProviderResponse {
  provider: "FINIX" | "STRIPE";
  finixApplicationId?: string;
}

interface IFinixPaymentRequest {
  orderId: string;
  token: string;
  amountInCents: number;
  saveForFuture?: boolean;
  payRemainingPrice?: boolean;
  savePaymentMethod?: boolean;
}

interface IFinixPaymentResponse {
  success: boolean;
  paymentId: string;
  finixTransferId: string;
}

interface IFinixPayRemainingWithSavedCardRequest {
  orderId: string;
  amountInCents: number;
}

interface IFinixSavedPaymentMethodResponse {
  data: {
    paymentMethodId: string;
    cardDetails: {
      brand: string;
      last4: string;
      expMonth?: number;
      expYear?: number;
    };
  } | null;
}

interface IFinixSplitAuthRequest {
  orderId: string;
  token1: string;
  amountInCents1: number;
  token2: string;
  amountInCents2: number;
  saveForFuture?: boolean;
  paymentPlan?: boolean;
  payRemainingPrice?: boolean;
  customAmount?: number;
}

interface IFinixSplitAuthResponse {
  success: boolean;
  authorizationIds: [string, string];
  paymentInstrumentIds: [string, string];
  buyerIdentityId: string;
  // Present only on 422 responses when a specific card failed
  failedCard?: 1 | 2;
  reason?: string;
  message?: string;
}

interface IFinixSplitCaptureRequest {
  orderId: string;
  authorizationIds: [string, string];
  amounts: [number, number];
  paymentInstrumentIds: [string, string];
  paymentPlan?: boolean;
  preferredPaymentMethodIndex?: 0 | 1;
  saveForFuture?: boolean;
  userId?: string;
}

interface IFinixSplitCaptureResponse {
  success: boolean;
  message: string;
}

interface IFinixSplitCancelRequest {
  orderId: string;
  authorizationIds: string[];
  failureReason?: string;
  userId?: string;
}

interface IFinixSplitCancelResponse {
  success: boolean;
  message: string;
}

const finixQueries = publicApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentProvider: builder.query<IPaymentProviderResponse, string>({
      query: (orderId) => ({
        url: `/finix/orders/${orderId}/payment-provider`,
        method: "GET",
      }),
    }),
    createFinixPayment: builder.mutation<
      IFinixPaymentResponse,
      IFinixPaymentRequest
    >({
      query: ({
        orderId,
        token,
        amountInCents,
        saveForFuture,
        payRemainingPrice,
        savePaymentMethod,
      }) => ({
        url: `/finix/orders/${orderId}/payment`,
        method: "POST",
        body: {
          token,
          amountInCents,
          saveForFuture,
          payRemainingPrice,
          savePaymentMethod,
        },
      }),
    }),
    payFinixRemainingWithSavedCard: builder.mutation<
      IFinixPaymentResponse,
      IFinixPayRemainingWithSavedCardRequest
    >({
      query: ({ orderId, amountInCents }) => ({
        url: `/finix/orders/${orderId}/pay-remaining-with-saved-card`,
        method: "POST",
        body: { amountInCents },
      }),
    }),
    getFinixSavedPaymentMethod: builder.query<
      IFinixSavedPaymentMethodResponse,
      string
    >({
      query: (orderId) => ({
        url: `/finix/orders/${orderId}/saved-payment-method`,
        method: "GET",
      }),
    }),
    createFinixSplitAuth: builder.mutation<
      IFinixSplitAuthResponse,
      IFinixSplitAuthRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/authorize`,
        method: "POST",
        body,
      }),
    }),
    captureFinixSplitPayment: builder.mutation<
      IFinixSplitCaptureResponse,
      IFinixSplitCaptureRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/capture`,
        method: "POST",
        body,
      }),
    }),
    cancelFinixSplitAuth: builder.mutation<
      IFinixSplitCancelResponse,
      IFinixSplitCancelRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `/finix/orders/${orderId}/split-payment/cancel`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetPaymentProviderQuery,
  useCreateFinixPaymentMutation,
  usePayFinixRemainingWithSavedCardMutation,
  useGetFinixSavedPaymentMethodQuery,
  useCreateFinixSplitAuthMutation,
  useCaptureFinixSplitPaymentMutation,
  useCancelFinixSplitAuthMutation,
} = finixQueries;
